import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  DEFAULT_VIEW_STATE_VERSION,
  clearViewStateEnvelope,
  readViewStateEnvelope,
  saveViewStateEnvelope,
} from '../utils/viewState';

type UsePersistentViewStateOptions<T> = {
  userId: string | null | undefined;
  routeKey: string;
  contextKey?: string;
  defaultState: T;
  version?: number;
  validate?: (state: unknown) => state is T;
};

type PersistentStateSetter<T> = T | ((current: T) => T);

function sessionStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readInitialState<T>({
  userId,
  routeKey,
  contextKey = 'default',
  defaultState,
  version = DEFAULT_VIEW_STATE_VERSION,
  validate,
}: UsePersistentViewStateOptions<T>): T {
  const storage = sessionStorageOrNull();
  if (!storage || !userId) return defaultState;
  return readViewStateEnvelope(storage, userId, routeKey, contextKey, version, validate)?.state ?? defaultState;
}

export function usePersistentViewState<T>({
  userId,
  routeKey,
  contextKey = 'default',
  defaultState,
  version = DEFAULT_VIEW_STATE_VERSION,
  validate,
}: UsePersistentViewStateOptions<T>) {
  const defaultStateRef = useRef(defaultState);
  defaultStateRef.current = defaultState;
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const storageKey = useMemo(
    () => `${userId || 'anonymous'}:${routeKey}:${contextKey}:v${version}`,
    [contextKey, routeKey, userId, version],
  );
  const [state, setState] = useState<T>(() => readInitialState({ userId, routeKey, contextKey, defaultState, version, validate }));
  const [loadedStateKey, setLoadedStateKey] = useState(storageKey);

  useEffect(() => {
    setState(readInitialState({ userId, routeKey, contextKey, defaultState: defaultStateRef.current, version, validate: validateRef.current }));
    setLoadedStateKey(storageKey);
  }, [storageKey]);

  const setPersistentState = useCallback((setter: PersistentStateSetter<T>) => {
    setState((current) => {
      const next = typeof setter === 'function' ? (setter as (value: T) => T)(current) : setter;
      const storage = sessionStorageOrNull();
      if (storage && userId) {
        saveViewStateEnvelope(storage, {
          userId,
          routeKey,
          contextKey,
          version,
          state: next,
        });
      }
      return next;
    });
  }, [contextKey, routeKey, userId, version]);

  const updatePersistentState = useCallback((patch: Partial<T> | ((current: T) => Partial<T>)) => {
    setPersistentState((current) => ({
      ...current,
      ...(typeof patch === 'function' ? patch(current) : patch),
    }));
  }, [setPersistentState]);

  const clearPersistentState = useCallback(() => {
    const storage = sessionStorageOrNull();
    if (storage) clearViewStateEnvelope(storage, userId, routeKey, contextKey, version);
    setState(defaultStateRef.current);
  }, [contextKey, routeKey, userId, version]);

  return {
    state,
    setState: setPersistentState,
    updateState: updatePersistentState,
    clearState: clearPersistentState,
    stateKey: storageKey,
    loadedStateKey,
  };
}

type UsePersistentScrollRestorationOptions = {
  userId: string | null | undefined;
  routeKey: string;
  contextKey?: string;
  ready?: boolean;
  enabled?: boolean;
  version?: number;
  elementRef?: RefObject<HTMLElement | null>;
};

type ScrollViewState = {
  scroll: {
    x: number;
    y: number;
  };
  elementScroll?: {
    x: number;
    y: number;
  };
};

function isScrollViewState(value: unknown): value is ScrollViewState {
  if (!value || typeof value !== 'object' || !('scroll' in value)) return false;
  const scroll = (value as { scroll?: unknown }).scroll;
  return Boolean(
    scroll
    && typeof scroll === 'object'
    && 'x' in scroll
    && 'y' in scroll
    && typeof (scroll as { x?: unknown }).x === 'number'
    && typeof (scroll as { y?: unknown }).y === 'number',
  );
}

export function usePersistentScrollRestoration({
  userId,
  routeKey,
  contextKey = 'default',
  ready = true,
  enabled = true,
  version = DEFAULT_VIEW_STATE_VERSION,
  elementRef,
}: UsePersistentScrollRestorationOptions) {
  const restoredRef = useRef(false);

  const saveScroll = useCallback(() => {
    if (!enabled || !userId) return;
    const storage = sessionStorageOrNull();
    if (!storage) return;
    saveViewStateEnvelope<ScrollViewState>(storage, {
      userId,
      routeKey: `${routeKey}:scroll`,
      contextKey,
      version,
      state: {
        scroll: {
          x: Math.max(0, Math.round(window.scrollX || 0)),
          y: Math.max(0, Math.round(window.scrollY || 0)),
        },
        elementScroll: elementRef?.current
          ? {
            x: Math.max(0, Math.round(elementRef.current.scrollLeft || 0)),
            y: Math.max(0, Math.round(elementRef.current.scrollTop || 0)),
          }
          : undefined,
      },
    });
  }, [contextKey, elementRef, enabled, routeKey, userId, version]);

  useEffect(() => {
    restoredRef.current = false;
  }, [contextKey, routeKey, userId, version]);

  useEffect(() => {
    if (!enabled || !ready || !userId || restoredRef.current) return undefined;
    const storage = sessionStorageOrNull();
    if (!storage) return undefined;
    const saved = readViewStateEnvelope<ScrollViewState>(storage, userId, `${routeKey}:scroll`, contextKey, version, isScrollViewState)?.state;
    const y = saved?.scroll.y ?? 0;
    const x = saved?.scroll.x ?? 0;
    const elementX = saved?.elementScroll?.x ?? 0;
    const elementY = saved?.elementScroll?.y ?? 0;
    if (x <= 0 && y <= 0 && elementX <= 0 && elementY <= 0) {
      restoredRef.current = true;
      return undefined;
    }

    let frame = 0;
    let attempts = 0;
    const restore = () => {
      attempts += 1;
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const element = elementRef?.current;
      const elementReady = !element || element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
      if ((maxY >= Math.min(y, 24) && elementReady) || attempts >= 12) {
        window.scrollTo(Math.min(x, Math.max(0, document.documentElement.scrollWidth - window.innerWidth)), Math.min(y, maxY));
        if (element) {
          element.scrollTo(
            Math.min(elementX, Math.max(0, element.scrollWidth - element.clientWidth)),
            Math.min(elementY, Math.max(0, element.scrollHeight - element.clientHeight)),
          );
        }
        restoredRef.current = true;
        return;
      }
      frame = window.requestAnimationFrame(restore);
    };
    frame = window.requestAnimationFrame(restore);
    return () => window.cancelAnimationFrame(frame);
  }, [contextKey, elementRef, enabled, ready, routeKey, userId, version]);

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let timer: number | null = null;
    const onScroll = () => {
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        saveScroll();
      }, 300);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const element = elementRef?.current;
    element?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('beforeunload', saveScroll);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      saveScroll();
      window.removeEventListener('scroll', onScroll);
      element?.removeEventListener('scroll', onScroll);
      window.removeEventListener('beforeunload', saveScroll);
    };
  }, [elementRef, enabled, saveScroll, userId]);
}
